import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Snackbar,
  Divider,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import DescriptionIcon from "@mui/icons-material/Description";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PrintIcon from "@mui/icons-material/Print";
import DrawIcon from "@mui/icons-material/Draw";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import PremiumPageLayout from "../../../templates/PremiumPageLayout";
import PremiumCard from "../../../molecules/PremiumCard";
import SectionHeader from "../../../molecules/SectionHeader";
import ContactDetailField from "../../../molecules/ContactDetailField";
import CarMediaUploaderViewer from "../../../molecules/CarMediaUploaderViewer";
import MySignatureCanvas from "../../../molecules/signature_canvas";
import PrintComponent from "../../PrintComponent";
import PrebidFormDownload from "../creation/save_as_pdf";
import { Bucket } from "../../../../api/config/apiConfig";
import {
  getPrebidPreview,
  imageUploadClientPreview,
  UpdateClientSignaturePrebid,
} from "./service";
import moment from "moment";

const ClientPreview = () => {
  const { token, email, uuid } = useParams();
  const navigate = useNavigate();

  const [formContent, setFormContent] = useState({
    issueDate: "",
    notice: "",
    contractPrice: "",
    date: "",
    importantNote: "",
    instructions: "",
    inclusions: "",
    exclusions: "",
    demurrage: "",
    paymentTerms: "",
    carDescription: {
      make: "",
      model: "",
      year: "",
      grade: "",
      specification: "",
      mileage: "",
      images: [],
    },
    firstParty: {
      name: "",
      email: "",
      mofaIdNo: "",
      sign: "",
      organization: "",
    },
    secondParty: {
      name: "",
      email: "",
      mofaIdNo: "",
      sign: "",
      organization: "",
    },
  });

  const [getter, setGetter] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isSignCanvas, setSignCanvas] = useState(false);

  const notify = (msg, severity = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const getPrebid = async () => {
    if (uuid) {
      getPrebidPreview(token, uuid, email)
        .then((res) => {
          if (res?.data?.data) {
            setFormContent(res.data.data);
          }
        })
        .catch((e) => {
          console.error("Failed to load prebid preview", e);
          notify("Failed to load prebid agreement", "error");
        });
    }
  };

  useEffect(() => {
    getPrebid();
  }, [getter]);

  const updateSignature = (key) => {
    const updated = {
      ...formContent,
      firstParty: {
        ...formContent.firstParty,
        sign: key,
      },
    };
    setFormContent(updated);
    setIsLoading(false);

    // Persist signature
    UpdateClientSignaturePrebid(token, uuid, email, updated)
      .then(() => {
        notify("Signature accepted & saved successfully!", "success");
        setGetter(!getter);
      })
      .catch((e) => {
        console.error("Signature save error", e);
        notify("Failed to save signature", "error");
      });
  };

  const handleUploadClientSignature = async (file) => {
    setIsLoading(true);
    const fd = new FormData();
    fd.append("image", file);

    imageUploadClientPreview(token, uuid, email, fd)
      .then((res) => {
        const key = res?.data?.data?.key;
        if (key) {
          updateSignature(key);
        }
      })
      .catch((error) => {
        notify(error?.response?.data?.message || "Failed to upload signature", "error");
        setIsLoading(false);
      });
  };

  const isClientAuthorized =
    formContent?.firstParty?.email?.toLowerCase() === email?.toLowerCase();
  const isSigned = Boolean(formContent?.firstParty?.sign);

  return (
    <>
      <PrintComponent>
        <PrebidFormDownload formContent={formContent} />
      </PrintComponent>

      <PremiumPageLayout
        title="Car Prebid Agreement & Review"
        actions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {isClientAuthorized && !isSigned && (
              <Button
                variant="contained"
                startIcon={<DrawIcon />}
                onClick={() => setSignCanvas(true)}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  fontWeight: 700,
                  px: 3,
                  py: 1,
                  background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
                  boxShadow: "0 4px 14px rgba(2, 132, 199, 0.3)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #0369a1 0%, #075985 100%)",
                  },
                }}
              >
                Sign & Accept Agreement
              </Button>
            )}

            {isSigned && (
              <Button
                variant="contained"
                disabled
                startIcon={<CheckCircleIcon />}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  fontWeight: 700,
                  bgcolor: "#dcfce7 !important",
                  color: "#15803d !important",
                }}
              >
                Agreement Accepted & Signed
              </Button>
            )}

            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => {
                if (window.innerWidth <= 768) {
                  notify("For a good experience, please use a desktop screen", "info");
                  return;
                }
                const tempTitle = document.title;
                document.title = `Prebid-Agreement-${formContent?.issueDate || "Doc"}-${moment().format("DD.MM.YYYY")}`;
                window.print();
                document.title = tempTitle;
              }}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 700,
                px: 2.5,
                borderColor: "#cbd5e1",
                color: "#334155",
              }}
            >
              Save as PDF
            </Button>
          </Box>
        }
      >
        {/* Vehicle Information */}
        <PremiumCard sx={{ mb: 3 }}>
          <SectionHeader
            icon={<DirectionsCarIcon />}
            title="Vehicle Specifications"
            subtitle="Details of the vehicle booked for auction & import."
          />

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                Make / Manufacturer
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {formContent?.carDescription?.make || "-"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                Model & Variant
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {formContent?.carDescription?.model || "-"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                Year
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {formContent?.carDescription?.year || "-"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                Grade
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {formContent?.carDescription?.grade || "-"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                Specification
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {formContent?.carDescription?.specification || "-"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                Mileage
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {formContent?.carDescription?.mileage || "-"}
              </Typography>
            </Grid>
          </Grid>
        </PremiumCard>

        {/* Car Media & Photos Gallery / Lightbox Viewer */}
        <PremiumCard sx={{ mb: 3 }}>
          <CarMediaUploaderViewer
            images={formContent?.carDescription?.images || []}
            isEdit={false}
            onNotify={notify}
            title="Vehicle Photos & Inspection Documents"
            subtitle="Click on any image to view in high resolution, zoom in, or download."
          />
        </PremiumCard>

        {/* Contract Pricing & Clauses */}
        <PremiumCard sx={{ mb: 3 }}>
          <SectionHeader
            icon={<DescriptionIcon />}
            title="Agreement Terms & Financials"
            subtitle="Review agreed pricing, dates, and terms."
          />

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                Contract Price
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#059669" }}>
                {formContent?.contractPrice || "-"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                Issue Date
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {formContent?.issueDate || "-"}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                Agreement Date
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {formContent?.date || "-"}
              </Typography>
            </Grid>
          </Grid>

          {formContent?.notice && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5 }}>
                Confidentiality Notice
              </Typography>
              <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "10px", color: "#334155" }}>
                {formContent.notice}
              </Paper>
            </Box>
          )}

          {formContent?.importantNote && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5 }}>
                Important Note
              </Typography>
              <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "10px", color: "#334155" }}>
                {formContent.importantNote}
              </Paper>
            </Box>
          )}

          {formContent?.instructions && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5 }}>
                Instructions
              </Typography>
              <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "10px", color: "#334155", whiteSpace: "pre-line" }}>
                {formContent.instructions}
              </Paper>
            </Box>
          )}

          {formContent?.paymentTerms && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5 }}>
                Payment Terms
              </Typography>
              <Paper elevation={0} sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "10px", color: "#334155", whiteSpace: "pre-line" }}>
                {formContent.paymentTerms}
              </Paper>
            </Box>
          )}
        </PremiumCard>

        {/* Dual Signatures */}
        <PremiumCard>
          <SectionHeader
            icon={<HowToRegIcon />}
            title="Signatures & Acceptance"
            subtitle="Dual verification from Buyer and Shelozon Logistics."
          />

          <Grid container spacing={4} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: "14px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>
                  1st Party (Buyer)
                </Typography>
                <Typography variant="body2" sx={{ color: "#475569" }}>
                  Name: <b>{formContent?.firstParty?.name || "-"}</b>
                </Typography>
                <Typography variant="body2" sx={{ color: "#475569" }}>
                  Email: <b>{formContent?.firstParty?.email || "-"}</b>
                </Typography>
                <Typography variant="body2" sx={{ color: "#475569" }}>
                  MOFA ID: <b>{formContent?.firstParty?.mofaIdNo || "-"}</b>
                </Typography>

                <Box
                  sx={{
                    height: 100,
                    backgroundColor: "#ffffff",
                    borderRadius: "10px",
                    border: "1px dashed #cbd5e1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 2,
                    p: 1,
                  }}
                >
                  {formContent?.firstParty?.sign ? (
                    <img
                      src={`${Bucket}${formContent.firstParty.sign}`}
                      alt="Buyer Signature"
                      style={{ maxHeight: "80px", maxWidth: "200px", objectFit: "contain" }}
                    />
                  ) : (
                    <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                      Pending client acceptance
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: "14px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>
                  2nd Party (Shelozon Logistics)
                </Typography>
                <Typography variant="body2" sx={{ color: "#475569" }}>
                  Representative: <b>{formContent?.secondParty?.name || "-"}</b>
                </Typography>
                <Typography variant="body2" sx={{ color: "#475569" }}>
                  Email: <b>{formContent?.secondParty?.email || "-"}</b>
                </Typography>
                <Typography variant="body2" sx={{ color: "#475569" }}>
                  Company: <b>{formContent?.secondParty?.organization || "-"}</b>
                </Typography>

                <Box
                  sx={{
                    height: 100,
                    backgroundColor: "#ffffff",
                    borderRadius: "10px",
                    border: "1px dashed #cbd5e1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 2,
                    p: 1,
                  }}
                >
                  {formContent?.secondParty?.sign ? (
                    <img
                      src={`${Bucket}${formContent.secondParty.sign}`}
                      alt="Shelozon Signature"
                      style={{ maxHeight: "80px", maxWidth: "200px", objectFit: "contain" }}
                    />
                  ) : (
                    <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                      Unsigned
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </PremiumCard>

        {/* Signature Modal for Client */}
        {isSignCanvas && (
          <MySignatureCanvas
            open={isSignCanvas}
            onClose={() => setSignCanvas(false)}
            onSuccess={handleUploadClientSignature}
          />
        )}

        {/* Global Notifications Snackbar */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={5000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={snackbarSeverity}
            onClose={() => setOpenSnackbar(false)}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </PremiumPageLayout>
    </>
  );
};

export default ClientPreview;
